import { useTranslation } from 'react-i18next';
import type { SearchResultDto } from '../api/search';
import type { MediaItem, MessageDto } from '../api/types';
import { formatSize } from '../lib/format';
import Avatar from './Avatar';

interface Props {
  result: SearchResultDto | null;
  loading: boolean;
  onOpenChat: (chatId: string) => void;
  onOpenMessage: (chatId: string, messageId: number) => void;
  onOpenContact: (userId: string) => void;
}

function textSnippet(m: MessageDto, fallback: string): string {
  const text = m.payload.data?.text ?? '';
  return text.length > 80 ? text.slice(0, 80) + '…' : text || fallback;
}

function firstMedia(m: MessageDto, kinds: string[]): MediaItem | null {
  return m.payload.data?.media?.find((x) => x.kind && kinds.includes(x.kind)) ?? null;
}

/** Результаты глобального поиска по шапке списка чатов (см. ChatList) — сгруппированы по
 *  категориям, тот же принцип, что у вкладок инфопанели (Медиа/Файлы/Ссылки), но по всем
 *  чатам пользователя сразу, а не по одному. */
export default function GlobalSearchResults({ result, loading, onOpenChat, onOpenMessage, onOpenContact }: Props) {
  const { t } = useTranslation('people');
  const nothingFound =
    result &&
    result.chats.length === 0 &&
    result.contacts.length === 0 &&
    result.messages.length === 0 &&
    result.media.length === 0 &&
    result.files.length === 0 &&
    result.links.length === 0;

  return (
    <div className="scroll-list" style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
      {loading && !result && <div style={{ padding: 24, color: 'var(--muted)', textAlign: 'center' }}>{t('search.loading')}</div>}
      {nothingFound && <div style={{ padding: 24, color: 'var(--muted)', textAlign: 'center' }}>{t('search.empty')}</div>}

      {result && result.chats.length > 0 && (
        <div>
          <div className="search-section-title">{t('search.chats')}</div>
          {result.chats.map((c) => (
            <div key={c.id} className="chat-item" onClick={() => onOpenChat(c.id)}>
              <Avatar userId={c.otherUserId ?? c.id} name={c.title || t('search.chat')} size={44} />
              <div className="chat-item-body">
                <div className="chat-item-row"><span className="chat-item-title">{c.title || t('search.chat')}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {result && result.contacts.length > 0 && (
        <div>
          {/* Поиск охватывает всех пользователей, а не только уже добавленные контакты. */}
          <div className="search-section-title">{t('search.people')}</div>
          {result.contacts.map((u) => (
            <div key={u.id} className="chat-item" onClick={() => onOpenContact(u.id)}>
              <Avatar userId={u.id} name={u.displayName || u.username} size={44} />
              <div className="chat-item-body">
                <div className="chat-item-row"><span className="chat-item-title">{u.displayName || u.username}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {result && result.messages.length > 0 && (
        <div>
          <div className="search-section-title">{t('search.messages')}</div>
          {result.messages.map((m) => (
            <div key={m.id} className="chat-item" onClick={() => onOpenMessage(m.chatId, m.id)}>
              <div className="chat-item-body">
                <div className="chat-item-row">
                  <span className="chat-item-title">{m.senderName || t('search.message')}</span>
                </div>
                <div className="chat-item-row">
                  <span className="chat-item-preview">{textSnippet(m, t('search.message'))}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {result && result.media.length > 0 && (
        <div>
          <div className="search-section-title">{t('search.media')}</div>
          {result.media.map((m) => {
            const item = firstMedia(m, ['image', 'video']);
            return (
              <div key={m.id} className="chat-item" onClick={() => onOpenMessage(m.chatId, m.id)}>
                <div className="chat-item-body">
                  <div className="chat-item-row">
                    <span className="chat-item-title">{item?.fileName || t('search.mediaFile')}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {result && result.files.length > 0 && (
        <div>
          <div className="search-section-title">{t('search.files')}</div>
          {result.files.map((m) => {
            const item = firstMedia(m, ['file']);
            return (
              <div key={m.id} className="chat-item" onClick={() => onOpenMessage(m.chatId, m.id)}>
                <div className="chat-item-body">
                  <div className="chat-item-row">
                    <span className="chat-item-title">{item?.fileName || t('search.file')}</span>
                  </div>
                  {item?.sizeBytes ? (
                    <div className="chat-item-row"><span className="chat-item-preview">{formatSize(item.sizeBytes)}</span></div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {result && result.links.length > 0 && (
        <div>
          <div className="search-section-title">{t('search.links')}</div>
          {result.links.map((m) => (
            <div key={m.id} className="chat-item" onClick={() => onOpenMessage(m.chatId, m.id)}>
              <div className="chat-item-body">
                <div className="chat-item-row">
                  <span className="chat-item-preview">{textSnippet(m, t('search.message'))}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
